import React from 'react';

/**
 * Single horizontal strip showing the user's plan tier and all daily/hourly
 * counters: 24/7 applies, resume tailoring, discoveries, hourly throttle,
 * countdown to UTC reset, and any queue-paused / cap warnings.
 *
 * Pure render — no fetching of its own. Parent passes:
 *   usage:           the /api/auto-apply/usage payload (or null while loading)
 *   resetCountdown:  seconds until 00:00 UTC, ticked by parent's setInterval
 */
export default function UsageStrip({ usage, resetCountdown }) {
  // Skeleton placeholder for first mount — keeps layout from jumping.
  if (!usage) {
    return (
      <div className="ajp-skel-strip">
        <div className="ajp-skeleton ajp-skel-strip-pill" />
        <div className="ajp-skeleton ajp-skel-strip-bar" />
      </div>
    );
  }

  const a = usage.applies24_7    || { used: 0, limit: 0 };
  const t = usage.resumeTailoring || { used: 0, limit: 0 };
  const h = usage.hourly          || { used: 0, limit: 5, resetsInSeconds: 0 };
  const pct      = a.limit ? a.used / a.limit : 0;
  const atCap    = a.limit > 0 && a.used >= a.limit;
  const warn80   = pct >= 0.8 && !atCap;
  const hourCap  = h.limit > 0 && h.used >= h.limit;
  const dh       = Math.floor(resetCountdown / 3600);
  const dm       = Math.floor((resetCountdown % 3600) / 60);
  const fmtReset = `${dh}h ${dm}m`;
  const nextBatchM = Math.max(0, Math.ceil((h.resetsInSeconds || 0) / 60));

  const planName = usage.plan || 'free';
  const palette = {
    free: { bg: 'rgba(255,255,255,0.08)',                            fg: 'rgba(255,255,255,0.7)' },
    lite: { bg: '#7c3aed',                                           fg: '#fff' },
    pro:  { bg: 'linear-gradient(135deg, #7c3aed, #c13dfd)',         fg: '#fff' },
    max:  { bg: 'linear-gradient(135deg, #c13dfd, #ff4fa0)',         fg: '#fff' },
  }[planName] || { bg: '#7c3aed', fg: '#fff' };

  const stripStyle = {
    display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14,
    padding: '10px 14px', borderRadius: 8,
    background: atCap ? 'rgba(248,113,113,0.12)' : warn80 ? 'rgba(250,204,21,0.10)' : 'rgba(255,255,255,0.04)',
    border: '1px solid ' + (atCap ? '#f87171' : warn80 ? '#facc15' : 'rgba(255,255,255,0.08)'),
    fontSize: 13, alignItems: 'center',
  };

  return (
    <div style={stripStyle}>
      <span style={{
        padding: '2px 10px', borderRadius: 999,
        background: palette.bg, color: palette.fg,
        fontWeight: 700, textTransform: 'uppercase', fontSize: 11,
      }}>
        {planName} plan
      </span>

      {planName === 'free' ? (
        <>
          <span style={{ color: 'rgba(255,255,255,0.7)' }}>
            24/7 auto-apply not included
          </span>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('openPricingModal'))}
            style={{
              marginLeft: 8, padding: '4px 12px', borderRadius: 999,
              fontSize: 11, fontWeight: 700,
              background: 'linear-gradient(135deg, #c13dfd, #ff4fa0)', color: '#fff',
              border: 'none', cursor: 'pointer',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
            ✨ Upgrade
          </button>
        </>
      ) : (
        <span style={{ color: atCap ? '#f87171' : '#fff' }}>
          <strong>{a.used}/{a.limit}</strong> 24/7 applies today
        </span>
      )}

      <span style={{ color: hourCap ? '#facc15' : 'rgba(255,255,255,0.7)' }}>
        · <strong>{h.used}/{h.limit}</strong> this hour
        {hourCap && nextBatchM > 0 && ` · next batch in ${nextBatchM}m`}
      </span>

      {usage.discoveries && (
        <span style={{ color: !usage.discoveries.allowed ? '#f87171' : 'rgba(255,255,255,0.7)' }}>
          · <strong>{usage.discoveries.used}/{usage.discoveries.limit}</strong> discoveries
        </span>
      )}

      <span style={{ color: 'rgba(255,255,255,0.7)' }}>
        · <strong>{t.used}/{t.limit}</strong> resume tailoring
      </span>

      <span style={{ color: 'rgba(255,255,255,0.5)' }}>
        · resets in {fmtReset} (00:00 UTC)
      </span>

      {atCap && (
        <span style={{ color: '#f87171', fontWeight: 600 }}>
          · ⏳ {usage.approvedAwaiting} job(s) queued for tomorrow
        </span>
      )}
      {warn80 && !atCap && (
        <span style={{ color: '#facc15', fontWeight: 600 }}>
          · ⚠️ {a.limit - a.used} applies left today
        </span>
      )}
      {usage.queuePaused && (
        <span style={{ color: '#facc15', fontWeight: 600 }}>
          · ⏸ queue is paused
        </span>
      )}
    </div>
  );
}